import { ApplicationRef, Component, OnDestroy, OnInit } from '@angular/core'
import { catchError, interval, of, Subscription, takeWhile } from 'rxjs'
import { Router } from '@angular/router'
import { TranslateService } from '@ngx-translate/core'
import { NgxSpinnerService } from 'ngx-spinner'
import { AuthService, EventBusService, SpinnerHandlerService, TokenService } from 'sbs-common'
import { LogActionService } from './shared/services/log-action/log-action.service'
import { BaseComponent } from './shared/components/base.component'
import { SwUpdate } from '@angular/service-worker'
import { MatDialog } from '@angular/material/dialog'
import { RefreshAppDialogComponent } from './home/refresh-app-dialog/refresh-app-dialog.component'
import { Title } from '@angular/platform-browser'
import { DirectionService } from './shared/services/direction.service'
import { ChaserProfileService } from './shared/services/chaser-profile.service'

const startShowSpinnerDelay = 200

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent extends BaseComponent implements OnInit, OnDestroy {
  title = 'Science Chaser'
  eventBusSub?: Subscription
  languageChangeSubscription?: Subscription
  isStable = false
  savedQrCode = ''

  private startShowSpinnerTimeout: number | undefined = undefined

  constructor(
    private tokenService: TokenService,
    private eventBusService: EventBusService,
    private router: Router,
    private translate: TranslateService,
    private spinner: NgxSpinnerService,
    private logActionService: LogActionService,
    private authService: AuthService,
    private updates: SwUpdate,
    public dialog: MatDialog,
    private appRef: ApplicationRef,
    private titleService: Title,
    public directionService: DirectionService,
    public chaserProfileService: ChaserProfileService,
    private spinnerHandler: SpinnerHandlerService,
  ) {
    super()
    this.titleService.setTitle(this.getTitle())
    this.spinnerHandler.showSpinner.subscribe(this.showSpinner.bind(this))
  }

  async ngOnInit() {
    const user = this.tokenService.getUser()
    this.eventBusSub = this.eventBusService.on('logout', (event: any) => {
      this.onLogout(event.onlyFrontend)
    })
    await this.setupLanguageHandling(user?.sub ?? null)
    this.enablePwaFeatures()
  }

  showSpinner = (state: boolean): void => {
    state ? this.spinner.show() : this.spinner.hide()

    if (state) {
      if (!this.startShowSpinnerTimeout) {
        // @ts-ignore
        this.startShowSpinnerTimeout = setTimeout(() => {
          this.spinner.show()
        }, startShowSpinnerDelay)
      }
    } else {
      if (this.startShowSpinnerTimeout) {
        clearTimeout(this.startShowSpinnerTimeout)
        this.startShowSpinnerTimeout = undefined
      }
      this.spinner.hide()
    }
  }

  onLogout(onlyFrontend = false): void {
    //maybe catching the error here could help.
    //maybe in case of refresh, we don't need to explicitly 'logout' of the server. only frontend will do.
    if (onlyFrontend) {
      this.clearLocalAndNavigateToLogin()
    } else {
      const deviceId = localStorage.getItem('currentDeviceIdKey') ?? undefined
      this.authService
        .logout(this.tokenService.getRefreshToken() ?? '', deviceId)
        .pipe(
          catchError((err) => {
            console.log('Could not logout from server. Error: ', err)
            this.clearLocalAndNavigateToLogin()
            return of()
          }),
        )
        .subscribe(() => {
          this.handleFullLogout()
        })
    }
  }

  ngOnDestroy(): void {
    if (this.eventBusSub) this.eventBusSub.unsubscribe()
    if (this.languageChangeSubscription) this.languageChangeSubscription.unsubscribe()
  }

  private getTitle(): string {
    const hostName = window.location.hostname
    const hostNameParts = hostName.split('.')
    const initialTitle = this.titleService.getTitle()
    return `${hostNameParts[0]}: ${initialTitle}`
  }

  private enablePwaFeatures() {
    if (this.updates.isEnabled) {
      console.log('PWA features are enabled.')
      this.updates.versionUpdates.subscribe((evt) => {
        switch (evt.type) {
          case 'VERSION_DETECTED':
            console.log(`Downloading new app version: ${evt.version.hash}`)
            break
          case 'VERSION_READY':
            console.log(`Current app version: ${evt.currentVersion.hash}`)
            console.log(`New app version ready for use: ${evt.latestVersion.hash}`)
            this.openAppUpdateDialog()
            break
          case 'VERSION_INSTALLATION_FAILED':
            console.log(`Failed to install app version '${evt.version.hash}': ${evt.error}`)
            break
        }
      })
      this.appRef.isStable.pipe(takeWhile(() => !this.isStable)).subscribe((isStable) => {
        if (isStable) {
          this.isStable = true
          const timeInterval = interval(1 * 60 * 60 * 1000) //Once an hour

          timeInterval.subscribe(() => {
            this.updates.checkForUpdate().then(() => console.log('Checking for update...'))
          })
        }
      })
    } else {
      console.log('PWA features are disabled.')
    }
  }

  private openAppUpdateDialog() {
    const dialogRef = this.dialog.open(RefreshAppDialogComponent, {
      disableClose: true,
    })
    dialogRef.afterClosed().subscribe((result) => {
      document.location.reload()
    })
  }

  private handleFullLogout() {
    const user = this.tokenService.getUser()
    if (user) {
      this.logActionService.logLogout(user.sub)
    }
    this.clearLocalAndNavigateToLogin()
  }

  private clearLocalAndNavigateToLogin() {
    this.tokenService.signOut()
    this.chaserProfileService.clearProfileInLocalStorage()
    this.eventBusService.emit({
      name: 'languageChange',
      value: window.navigator.language.substring(0, 2) ?? 'en',
    })
    this.router.navigate(['login'])
  }

  private async setupLanguageHandling(id: string | null) {
    this.languageChangeSubscription = this.eventBusService.on('languageChange', (language: any) => {
      this.translate.use(language)
      this.handleDirection(language)
      console.log('Changing to the language:', language)
    })
    if (!id) {
      const currentLanguage = this.getDefaultLanguage()
      this.eventBusService.emit({
        name: 'languageChange',
        value: currentLanguage,
      })
    }
  }

  private getDefaultLanguage(): string {
    return window.navigator.language.substring(0, 2) ?? 'en'
  }

  private handleDirection(language: string) {
    if (language === 'he') {
      this.directionService.set('rtl')
    } else {
      this.directionService.set('ltr')
    }
  }
}
